import React, { useState } from 'react';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  const faqs = [
    {
      q: "Does Typeit4u read my messages?",
      a: "No. Typeit4u never reads your messages, notifications, or keyboard input automatically. The AI only processes text that YOU manually paste into a feature. You are always in control of what the AI sees."
    },
    {
      q: "Is Typeit4u free?",
      a: "Yes. Typeit4u is free to download with 15 AI requests per day. Upgrade to Pro at ₹99/month for unlimited access to all features."
    },
    {
      q: "Which languages does it support?",
      a: "Typeit4u supports Tamil, Hindi, and English. You can type in Tamil or Hindi and translate to fluent English with one tap."
    },
    {
      q: "Which apps does it work with?",
      a: "Typeit4u works with every app on Android — WhatsApp, Instagram, Gmail, Telegram, Twitter/X, LinkedIn, and more. Any app with a text field will work."
    },
    {
      q: "Is my data safe?",
      a: "Yes. OCR (image to text) is processed 100% offline on your device. When you use AI features like Improve or Translate, only the specific text you paste is sent to our server over HTTPS and is never stored permanently."
    },
    {
      q: "What Android version is required?",
      a: "Typeit4u works on Android 7.0 (Nougat) and above."
    },
    {
      q: "How do I set Typeit4u as my default keyboard?",
      a: "Go to Settings → System → Language & Input → On-screen keyboard → Manage keyboards → Enable Typeit4u. Then set it as default when your current keyboard is active."
    },
    {
      q: "Who made Typeit4u?",
      a: "Typeit4u is a product of Deyvainfotech, an Indian technology company based in Chennai. We build digital products for Indian users."
    }
  ];

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-4xl mx-auto text-center"
          >
            <HelpCircle className="w-16 h-16 mx-auto mb-6" style={{ color: '#FF6B9D' }} />
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
              style={{ color: '#0A0A0A' }}
            >
              Frequently Asked Questions
            </h1>
            <p className="text-base sm:text-lg text-gray-600">
              Everything you need to know about Typeit4u
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20">
        <div className="container max-w-3xl">
          <div className="space-y-4">
            {faqs.map((item, index) => {
              const isOpen = openIndex === index;
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.05 }}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <h3 className="text-base sm:text-lg font-semibold pr-4" style={{ color: '#0A0A0A' }}>
                      {item.q}
                    </h3>
                    <ChevronDown
                      className={`w-5 h-5 flex-shrink-0 transition-transform ${
                        isOpen ? 'transform rotate-180' : ''
                      }`}
                      style={{ color: '#FF6B9D' }}
                    />
                  </button>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-6 pb-6"
                    >
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Still Have Questions */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mt-16 text-center bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-12"
          >
            <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Still Have Questions?
            </h3>
            <p className="text-gray-600 mb-8">
              Our team is happy to help. Reach out and we will get back to you within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center gap-2 justify-center"
                style={{
                  background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '26px',
                  padding: '16px 32px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Contact Us
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.typeit4u.keyboard"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 justify-center bg-white text-gray-900 border-2 border-gray-200 rounded-full px-8 py-4 font-medium hover:border-orange-300 transition-colors"
              >
                Download Free
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
